import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FiAlertCircle,
  FiArrowLeft,
  FiCalendar,
  FiCheck,
  FiInfo,
  FiMapPin,
  FiMessageSquare,
  FiMoon,
  FiUsers,
} from 'react-icons/fi';
import PackageSummaryBar from '../../components/common/PackageSummaryBar';
import PackageState from '../../components/common/PackageState';
import PackageImage from '../../components/common/PackageImage';
import PackagePassengerForm, {
  emptyPassenger,
} from '../../components/PackagePassengerForm/PackagePassengerForm';
import PackageFareSummary from '../../components/PackageFareSummary/PackageFareSummary';
import PackageReviewModal from '../../components/PackageReviewModal/PackageReviewModal';
import PackagePaymentModes from '../../components/PackagePaymentModes/PackagePaymentModes';
import { bookPackageApi } from '../../api/packagesApi';
import {
  PACKAGE_LIMITS,
  PACKAGE_ROUTES,
  WALLET_MODE,
  bookingDetailsRoute,
  paymentModeLabel,
} from '../../constants/packageConstants';
import {
  balanceDueDate,
  buildPackageBookRequest,
  daysNightsText,
  formatDate,
  fromIsoDate,
  isPackageFailed,
  newClientBookingId,
  passengerTypesFor,
  paxSummary,
  paymentRedirectUrl,
  validatePassengers,
} from '../../utils/packageUtils';
import {
  clearPackageBookContext,
  loadPackageBookContext,
} from '../../utils/packageBookContext';
import '../../styles/packagesShared.scss';
import './PackageBookingPage.scss';

function PackageBookingPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [ctx] = useState(() => {
    const fromState =
      location.state?.quote?.nextraQuoteId && location.state?.packageKey
        ? location.state
        : null;
    return fromState || loadPackageBookContext();
  });
  // One id per checkout: a retry after an explicit failure re-sends the same
  // id, so the server can recognise a repeat instead of booking twice.
  const [clientBookingId] = useState(newClientBookingId);
  const [passengers, setPassengers] = useState(() =>
    ctx ? passengerTypesFor(ctx.pax).map(emptyPassenger) : [],
  );
  const [requests, setRequests] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState(null); // { tone, text, reference }
  const [reviewOpen, setReviewOpen] = useState(false);
  const [paymentMode, setPaymentMode] = useState(WALLET_MODE);
  const [selectedPg, setSelectedPg] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [booking, setBooking] = useState(false);
  // Once a book went out without a clear answer this page never books again.
  const [locked, setLocked] = useState(false);
  const inFlight = useRef(false);

  const quote = ctx?.quote || null;
  const fare = quote?.fare || {};
  const summary = ctx?.summary || {};
  const total = Number(fare.grandTotal) > 0 ? Number(fare.grandTotal) : null;
  const departureDate = fromIsoDate(summary.departureDate);
  const returnDate = fromIsoDate(summary.returnDate);
  // never in the past: once the window is over the balance is due today
  const balanceDue = balanceDueDate(
    departureDate,
    summary.balanceDueDaysBeforeTravel,
  );
  const tripLine = [formatDate(departureDate), formatDate(returnDate)]
    .filter(Boolean)
    .join(' → ');

  const travellersDone = useMemo(
    () => Object.keys(validatePassengers(passengers)).length === 0,
    [passengers],
  );

  const updatePassenger = useCallback((index, patch) => {
    setPassengers((prev) =>
      prev.map((p, i) => (i === index ? { ...p, ...patch } : p)),
    );
    // clear only the errors of the fields that changed
    const FIELD = {
      firstName: 'first',
      lastName: 'last',
      mobileCountry: 'mobile',
      dialCode: 'mobile',
    };
    setErrors((e) => {
      const keys = Object.keys(patch)
        .map((k) => `p${index}-${FIELD[k] || k}`)
        .filter((k) => e[k]);
      if (!keys.length) return e;
      const next = { ...e };
      keys.forEach((k) => delete next[k]);
      return next;
    });
  }, []);

  // stable: the shared gateway selector lists it in an effect's dependencies
  const handlePgChange = useCallback((pg) => {
    setSelectedPg(pg);
    setPaymentError('');
  }, []);

  const backToPackage = () => {
    if (ctx?.packageKey) {
      navigate(-1);
      return;
    }
    navigate(PACKAGE_ROUTES.LANDING, { replace: true });
  };

  const handleReview = () => {
    if (locked || booking) return;
    setBanner(null);
    const next = validatePassengers(passengers);
    setErrors(next);
    const first = Object.keys(next)[0];
    if (first) {
      const index = /^p(\d+)-/.exec(first)?.[1] || '0';
      document
        .getElementById(`pkg-passenger-${index}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setBanner({
        tone: 'error',
        text: 'Please correct the highlighted traveller details.',
      });
      return;
    }
    if (paymentMode !== WALLET_MODE && !selectedPg) {
      const text = `No payment gateway is set up for ${paymentModeLabel(
        paymentMode,
      )}. Choose another payment method.`;
      setPaymentError(text);
      setBanner({ tone: 'error', text });
      document
        .getElementById('pkg-payment')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (!termsAccepted) {
      setBanner({
        tone: 'error',
        text: 'Please accept the terms and conditions to continue.',
      });
      return;
    }
    setReviewOpen(true);
  };

  const placeBooking = async () => {
    if (inFlight.current || locked) return;
    inFlight.current = true;
    setBooking(true);
    setBanner(null);
    const body = buildPackageBookRequest({
      clientBookingId,
      ctx,
      passengers,
      specialRequests: requests,
      paymentMode,
      pgName: selectedPg,
    });
    const online = paymentMode !== WALLET_MODE;
    let read;
    try {
      read = await bookPackageApi(body);
    } finally {
      setBooking(false);
      inFlight.current = false;
    }

    const result = read.ok ? read.data : null;
    const ref = result?.pkgBookingId || '';

    // online payment: the booking is held and the agent pays at the gateway.
    // The page locks before leaving so a back-navigation can't book twice.
    if (online && read.ok && !isPackageFailed(result?.status)) {
      const payUrl = paymentRedirectUrl(result);
      clearPackageBookContext();
      setLocked(true);
      if (payUrl) {
        window.location.href = payUrl;
        return;
      }
      setReviewOpen(false);
      setBanner({
        tone: 'info',
        text:
          read.message ||
          'The booking was received but the payment page could not be opened. Do not book again — share this reference with support.',
        reference: ref || clientBookingId,
      });
      return;
    }

    // booked (confirmed or still pending): the details page takes it from here
    if (ref && !isPackageFailed(result.status)) {
      clearPackageBookContext();
      setLocked(true);
      setReviewOpen(false);
      navigate(bookingDetailsRoute(ref), {
        replace: true,
        state: { booked: true, booking: result, summary },
      });
      return;
    }

    setReviewOpen(false);

    // the server answered, and the booking was refused
    if (ref && isPackageFailed(result.status)) {
      setBanner({
        tone: 'error',
        text: read.message || 'The supplier could not confirm this booking.',
        reference: ref,
      });
      return;
    }

    // no answer, or an answer without a booking: it may have gone through
    if (read.network || (read.ok && !ref)) {
      setLocked(true);
      clearPackageBookContext();
      setBanner({
        tone: 'info',
        text: 'We did not get a clear answer for this booking, so it may have gone through. Do not book again — share this reference with support to check it first.',
        reference: clientBookingId,
      });
      return;
    }

    if (read.expired) {
      setBanner({
        tone: 'error',
        text:
          read.message ||
          'This price has expired. Go back to the package and continue again for a fresh price.',
        reference: read.correlationId,
      });
      return;
    }

    setBanner({
      tone: 'error',
      text: read.message || 'Booking failed. Please try again.',
      reference: read.correlationId,
    });
  };

  if (!ctx || !quote) {
    return (
      <div className="pkg-booking">
        <Container className="pkg-booking__main">
          <PackageState
            title="Your selection has expired"
            message="Pick the package, departure and travellers again to get a fresh price."
            primaryText="Search packages"
            onPrimary={() => navigate(PACKAGE_ROUTES.LANDING)}
          />
        </Container>
      </div>
    );
  }

  return (
    <main className="pkg-booking">
      <PackageSummaryBar
        onBack={backToPackage}
        backLabel="Back to package"
        items={[
          { icon: <FiMapPin />, label: 'Package', value: summary.name },
          { icon: <FiCalendar />, label: 'Travel', value: tripLine },
          {
            icon: <FiMapPin />,
            label: 'Joining',
            value: [summary.city, summary.grade].filter(Boolean).join(' · '),
          },
          {
            icon: <FiUsers />,
            label: 'Travellers',
            value: paxSummary(ctx.pax),
          },
        ]}
      />

      <Container className="pkg-booking__main">
        <Row className="g-4">
          <Col lg={8} md={12}>
            <section className="pkg-booking__hero">
              <div className="pkg-booking__hero-media">
                <PackageImage
                  src={summary.cover}
                  alt={summary.name || ''}
                  loading="eager"
                />
              </div>
              <div className="pkg-booking__hero-body">
                <div className="pkg-booking__hero-top">
                  <div>
                    {summary.destination && (
                      <small>{summary.destination}</small>
                    )}
                    <h2>{summary.name}</h2>
                  </div>
                  <button
                    type="button"
                    className="pkg-booking__change"
                    onClick={backToPackage}
                  >
                    Change
                  </button>
                </div>
                <div className="pkg-booking__hero-facts">
                  {tripLine && (
                    <span>
                      <FiCalendar /> {tripLine}
                    </span>
                  )}
                  {summary.days != null && (
                    <span>
                      <FiMoon /> {daysNightsText(summary.days, summary.nights)}
                    </span>
                  )}
                  {(summary.city || summary.grade) && (
                    <span>
                      <FiMapPin />{' '}
                      {[summary.city, summary.grade]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  )}
                  <span>
                    <FiUsers /> {paxSummary(ctx.pax)}
                  </span>
                </div>
                {summary.supplier && (
                  <span className="pkg-booking__supplier">
                    Operated by {summary.supplier}
                  </span>
                )}
              </div>
            </section>

            {banner && (
              <div
                className={`pkg-alert ${
                  banner.tone === 'info' ? 'pkg-alert--info' : ''
                } pkg-booking__banner`}
                role="alert"
              >
                {banner.tone === 'info' ? <FiInfo /> : <FiAlertCircle />}
                <span>
                  {banner.text}
                  {banner.reference && (
                    <small className="d-block mt-1">
                      Reference: <strong>{banner.reference}</strong>
                    </small>
                  )}
                </span>
              </div>
            )}

            <section className="pkg-booking__card">
              <header className="pkg-booking__card-head">
                <span className="pkg-booking__badge">
                  <FiUsers /> Traveller Details
                </span>
                <p>
                  Enter every traveller exactly as on their passport or ID. The
                  lead traveller&apos;s email and mobile are shared with the
                  supplier.
                </p>
              </header>
              {passengers.map((p, i) => (
                <PackagePassengerForm
                  // the list never reorders: the index is the identity
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  index={i}
                  passenger={p}
                  onChange={updatePassenger}
                  errors={errors}
                  lead={i === 0}
                />
              ))}
            </section>

            <section className="pkg-booking__card">
              <header className="pkg-booking__card-head">
                <span className="pkg-booking__badge">
                  <FiMessageSquare /> Special Requests
                </span>
                <p>
                  Optional. Meal preferences, room requests or anything the
                  supplier should know. Requests are not guaranteed.
                </p>
              </header>
              <label className="pkg-booking__requests" htmlFor="pkg-requests">
                <textarea
                  id="pkg-requests"
                  rows={3}
                  maxLength={PACKAGE_LIMITS.MAX_REQUESTS}
                  value={requests}
                  placeholder="e.g. Vegetarian meals"
                  onChange={(e) =>
                    setRequests(
                      e.target.value.slice(0, PACKAGE_LIMITS.MAX_REQUESTS),
                    )
                  }
                />
                <small>
                  {requests.length}/{PACKAGE_LIMITS.MAX_REQUESTS}
                </small>
              </label>
            </section>

            <div id="pkg-payment">
              <PackagePaymentModes
                mode={paymentMode}
                onModeChange={(mode) => {
                  setPaymentMode(mode);
                  setSelectedPg('');
                  setPaymentError('');
                }}
                pg={selectedPg}
                onPgChange={handlePgChange}
                error={paymentError}
              />
            </div>
          </Col>

          <Col lg={4} md={12}>
            <div className="pkg-booking__sidebar">
              <PackageFareSummary
                fare={fare}
                balanceDue={balanceDue}
                travellersDone={travellersDone}
              />
            </div>
          </Col>
        </Row>
      </Container>

      <div className="pkg-booking__bottom-bar">
        <Container className="pkg-booking__bottom-inner">
          <label className="pkg-booking__terms" htmlFor="pkg-terms">
            <input
              id="pkg-terms"
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => {
                setTermsAccepted(e.target.checked);
                setBanner(null);
              }}
            />
            <span>
              I confirm the traveller details and agree to the{' '}
              <a
                href="/terms-conditions"
                target="_blank"
                rel="noopener noreferrer"
              >
                terms and conditions
              </a>
              .
            </span>
          </label>
          <div className="pkg-booking__bottom-actions">
            <button
              type="button"
              className="pkg-booking__back-btn"
              onClick={backToPackage}
            >
              <FiArrowLeft />
              <span>Back</span>
            </button>
            <button
              type="button"
              className="pkg-booking__pay-btn"
              onClick={handleReview}
              disabled={locked || booking || total == null}
            >
              <FiCheck />
              <span>Review &amp; Book</span>
            </button>
          </div>
        </Container>
      </div>

      <PackageReviewModal
        show={reviewOpen}
        onHide={() => {
          if (!booking) setReviewOpen(false);
        }}
        onConfirm={placeBooking}
        submitting={booking}
        summary={summary}
        tripLine={tripLine}
        pax={ctx.pax}
        passengers={passengers}
        requests={requests}
        fare={fare}
        paymentMode={paymentMode}
        pgName={selectedPg}
      />
    </main>
  );
}

export default PackageBookingPage;
